import type { AnySelfOwnedWidgetClass, SelfOwnedWidget, WidgetId } from '../selfOwnedWidget';
import type {
    WidgetContainer,
    WidgetContainerInitArgs,
    WidgetContainerJSON,
    WidgetContainerLifecycle,
} from './WidgetContainer';
import { WidgetContainerModel } from './WidgetContainerModel';
import { batch, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { Layout, WidgetTab, WidgetType } from '@thinkalpha/ui-types/contracts/workspace';
import { optional } from 'inversify';
import { inject, injectable } from 'src/features/ioc';
import { ErrorWidgetModel } from 'src/models/ErrorWidgetModel';
import { PlaceholderWidgetModel } from 'src/models/PlaceholderWidgetModel';
import type { ReactWidgetContainer } from 'src/ReactContainer';
import { setMaximizedContainer } from 'src/store/actions/container';
import { addTab, updateActiveTab, updateTabName } from 'src/store/actions/tab';
import { onTabChannelChange, resetChannelData } from 'src/store/actions/widgetAndChannel';
import type { IsomorphicBindings, ReactBindings } from 'src/types/bindings';
import { v4 as uuidv4 } from 'uuid';

const STILL_UNINITIALIZED = (action: string) =>
    new Error(`WidgetContainer.${action} called while uninitialized; init() must be called first`);
const ALREADY_DISPOSED = (action: string) =>
    new Error(`WidgetContainer.${action} called on a disposed WidgetContainer`);

const asError = (error: unknown): Error => (error instanceof Error ? error : new Error(String(error)));

interface SelfOwnedWidgetEntry {
    type: WidgetType;
    widget: SelfOwnedWidget<unknown>;
    reactContainer: ReactWidgetContainer;
}

@injectable()
export class WidgetContainerImpl implements WidgetContainer, AsyncDisposable {
    readonly #lifecycle = signal<WidgetContainerLifecycle>({ kind: 'uninitialized' });
    #model: WidgetContainerModel | undefined;

    readonly #selfOwnedWidgetEntries = new Map<WidgetId, SelfOwnedWidgetEntry>();
    readonly #widgets = signal<ReadonlyMap<WidgetId, SelfOwnedWidget<unknown>>>(new Map());
    #disposeEntitlementReconciler: (() => void) | undefined;

    // Empty defaults shared across pre-init access. Returning the same instance
    // each time keeps signal identity stable for consumers that subscribe before
    // `init` has fired (e.g., async IPC paths).
    readonly #emptyTabs = signal<readonly WidgetTab[]>([]);
    readonly #emptyActiveTabId = signal<string | null>(null);
    readonly #emptyIsMaximized = signal<boolean>(false);
    readonly #emptyDirty = signal<boolean>(false);
    readonly #emptyLayout = signal<Layout>({ x: 0, y: 0, w: 0, h: 0 });

    constructor(
        @inject('Store') private readonly store: IsomorphicBindings['Store'],
        @inject('WorkspaceService') private readonly workspaceService: IsomorphicBindings['WorkspaceService'],
        @inject('ReactWidgetContainerFactory')
        private readonly reactWidgetContainerFactory: ReactBindings['ReactWidgetContainerFactory'],
        @inject('SelfOwnedWidgetRegistry')
        private readonly selfOwnedWidgetRegistry: IsomorphicBindings['SelfOwnedWidgetRegistry'],
        @inject('Logger') private readonly logger: IsomorphicBindings['Logger'],
        // Optional for narrow unit-test and standalone compositions. Production
        // WidgetContainers are created only in React windows, where this is bound.
        @inject('AllowedWidgetsModel')
        @optional()
        private readonly allowedWidgetsModel?: IsomorphicBindings['AllowedWidgetsModel'],
    ) {}

    get lifecycle(): ReadonlySignal<WidgetContainerLifecycle> {
        return this.#lifecycle;
    }

    get position(): ReadonlySignal<Layout> {
        return this.#model?.position ?? this.#emptyLayout;
    }

    get tabs(): ReadonlySignal<readonly WidgetTab[]> {
        return this.#model?.tabs ?? this.#emptyTabs;
    }

    get activeTabId(): ReadonlySignal<string | null> {
        return this.#model?.activeTabId ?? this.#emptyActiveTabId;
    }

    get isMaximized(): ReadonlySignal<boolean> {
        return this.#model?.isMaximized ?? this.#emptyIsMaximized;
    }

    get dirty(): ReadonlySignal<boolean> {
        return this.#model?.dirty ?? this.#emptyDirty;
    }

    async init(args: WidgetContainerInitArgs): Promise<void> {
        const state = this.#lifecycle.value;
        if (state.kind !== 'uninitialized') {
            // init is non-idempotent: a second call indicates a programming error.
            throw new Error(`WidgetContainer.init called from lifecycle state '${state.kind}' (must be uninitialized)`);
        }

        this.#model = new WidgetContainerModel({
            id: args.id,
            windowId: args.windowId,
            layout: args.layout,
            store: this.store,
        });

        this.#lifecycle.value = { kind: 'initialized', id: args.id, windowId: args.windowId };

        // Swap placeholder/real instances whenever the entitlement set changes.
        // Registration applies the current entitlements, so the reconciler only
        // has to handle changes after the fact.
        const allowedWidgetsModel = this.allowedWidgetsModel;
        if (allowedWidgetsModel) {
            this.#disposeEntitlementReconciler = effect(() => {
                const isReady = allowedWidgetsModel.isReady.value;
                const allowed = allowedWidgetsModel.allowedWidgetTypes.value;
                if (!isReady) return;
                this.#reconcileEntitlements(allowed);
            });
        }

        // Hydration currently flows through WorkspaceService's ledger and
        // Redux fan-out; keep `initialJSON` available for the native path.
        void args.initialJSON;
    }

    async dispose(): Promise<void> {
        const state = this.#lifecycle.value;
        if (state.kind === 'disposed' || state.kind === 'disposing') return;

        this.#lifecycle.value = { kind: 'disposing' };
        this.#disposeEntitlementReconciler?.();
        this.#disposeEntitlementReconciler = undefined;
        this.#disposeAllWidgets();
        this.#model?.[Symbol.dispose]();
        this.#model = undefined;
        this.#lifecycle.value = { kind: 'disposed' };
    }

    async [Symbol.asyncDispose](): Promise<void> {
        await this.dispose();
    }

    async addInitialWidget(type: WidgetType, tabId: WidgetId, initialState: unknown): Promise<void> {
        this.#requireInitialized('addInitialWidget');
        const widgetClass = this.selfOwnedWidgetRegistry.get(type);
        if (!widgetClass) {
            return;
        }
        this.#registerSelfOwnedWidget(type, widgetClass, tabId, initialState);
    }

    async addTab(type: WidgetType, _opts?: { x?: number; y?: number }): Promise<WidgetId> {
        this.#requireInitialized('addTab');
        const tabId = uuidv4() as WidgetId;
        const widgetClass = this.selfOwnedWidgetRegistry.get(type);

        if (widgetClass) {
            const initialState = widgetClass.defaultInitialState?.() ?? widgetClass.defaultState();
            this.#registerSelfOwnedWidget(type, widgetClass, tabId, initialState);
        }

        const containerId = this.#assertedContainerId('addTab');
        const widget = widgetClass
            ? ({ type, channelId: null } as unknown as WidgetTab['widget'])
            : ({ type } as unknown as WidgetTab['widget']);
        this.store.dispatch(
            addTab(
                {
                    id: tabId,
                    name: undefined,
                    widget,
                    containerId,
                    channelId: null,
                    isActive: true,
                } as never,
                containerId,
            ),
        );

        return tabId;
    }

    async removeWidget(tabId: WidgetId): Promise<void> {
        this.#requireInitialized('removeWidget');
        await this.workspaceService.removeWidget(tabId);
    }

    async disposeWidget(tabId: WidgetId): Promise<void> {
        this.#requireInitialized('disposeWidget');
        const entry = this.#selfOwnedWidgetEntries.get(tabId);
        if (entry) {
            this.#disposeWidget(tabId, entry);
        }
    }

    getWidget<T extends SelfOwnedWidget<unknown>>(tabId: WidgetId): T | undefined {
        return this.#selfOwnedWidgetEntries.get(tabId)?.widget as T | undefined;
    }

    getWidgetReactContainer(tabId: WidgetId): ReactWidgetContainer | undefined {
        return this.#selfOwnedWidgetEntries.get(tabId)?.reactContainer;
    }

    get widgets(): ReadonlySignal<ReadonlyMap<WidgetId, SelfOwnedWidget<unknown>>> {
        return this.#widgets;
    }

    setTabChannel(tabId: WidgetId, channel: string | null): void {
        this.#requireInitialized('setTabChannel');
        this.store.dispatch(onTabChannelChange(tabId, channel));
    }

    resetTabChannelData(tabId: WidgetId): void {
        this.#requireInitialized('resetTabChannelData');
        this.store.dispatch(resetChannelData(tabId));
    }

    setTabName(tabId: WidgetId, name: string): void {
        this.#requireInitialized('setTabName');
        this.store.dispatch(updateTabName(tabId, name));
    }

    setActiveTab(tabId: WidgetId): void {
        this.#requireInitialized('setActiveTab');
        const containerId = this.#assertedContainerId('setActiveTab');
        this.store.dispatch(updateActiveTab(containerId, tabId));
    }

    async removeTab(tabId: WidgetId): Promise<void> {
        this.#requireInitialized('removeTab');
        await this.workspaceService.removeWidget(tabId);
    }

    toggleMaximized(): void {
        this.#requireInitialized('toggleMaximized');
        const containerId = this.#assertedContainerId('toggleMaximized');
        const next = this.isMaximized.value ? undefined : containerId;
        this.store.dispatch(setMaximizedContainer(next));
    }

    async deleteSelf(): Promise<void> {
        this.#requireInitialized('deleteSelf');
        const containerId = this.#assertedContainerId('deleteSelf');
        await this.workspaceService.removeContainer(containerId);
    }

    async toJSON(): Promise<WidgetContainerJSON> {
        this.#requireInitialized('toJSON');
        const json = await this.#model!.toJSON();
        return {
            ...json,
            tabs: json.tabs.map((tab) => {
                const tabId = tab.id as WidgetId;
                const widget = this.#selfOwnedWidgetEntries.get(tabId)?.widget;
                if (!widget) return tab;

                const widgetClass = this.selfOwnedWidgetRegistry.get(tab.widget.type);
                if (!widgetClass) return tab;

                try {
                    const persistedState = widgetClass.stateSchema.parse(widget.toJSON()) as Record<string, unknown>;
                    return {
                        ...tab,
                        widget: { ...tab.widget, ...persistedState } as WidgetTab['widget'],
                    };
                } catch {
                    // Stand-ins hold the persisted blob verbatim — possibly the
                    // very state the schema rejects (an error stand-in exists
                    // because construction failed). Serialize it raw rather than
                    // trading it for the slim redux tab, which would lose the
                    // user's saved state on the next save.
                    if (widget instanceof PlaceholderWidgetModel || widget instanceof ErrorWidgetModel) {
                        return {
                            ...tab,
                            widget: {
                                ...tab.widget,
                                ...(widget.toJSON() as Record<string, unknown>),
                            } as WidgetTab['widget'],
                        };
                    }
                    return tab;
                }
            }),
        };
    }

    async markCleanAll(): Promise<void> {
        this.#requireInitialized('markCleanAll');
        await this.#model!.markCleanAll();
    }

    #requireInitialized(action: string): void {
        const state = this.#lifecycle.value;
        if (state.kind === 'uninitialized') throw STILL_UNINITIALIZED(action);
        if (state.kind === 'disposed' || state.kind === 'disposing') throw ALREADY_DISPOSED(action);
    }

    #assertedContainerId(action: string): string {
        const state = this.#lifecycle.value;
        if (state.kind !== 'initialized') throw STILL_UNINITIALIZED(action);
        return state.id;
    }

    /**
     * Register the widget instance occupying a tab. Which instance depends on
     * the user's entitlements: the real model when the type is allowed, a
     * {@link PlaceholderWidgetModel} otherwise (or while entitlements are
     * still resolving). A real construction failure registers an
     * {@link ErrorWidgetModel} — preserving the persisted state — and then
     * rethrows so callers keep their abort/rollback semantics.
     */
    #registerSelfOwnedWidget(
        type: WidgetType,
        widgetClass: AnySelfOwnedWidgetClass,
        tabId: WidgetId,
        initialState: unknown,
    ): void {
        const entitled = this.allowedWidgetsModel
            ? this.allowedWidgetsModel.isReady.peek() && this.allowedWidgetsModel.allowedWidgetTypes.peek().has(type)
            : true;

        if (!entitled) {
            this.#setEntry(tabId, {
                type,
                widget: new PlaceholderWidgetModel({
                    container: this,
                    tabId,
                    widgetType: type,
                    persistedState: initialState,
                    logger: this.logger,
                }),
                reactContainer: this.#createWidgetReactContainer(tabId, initialState),
            });
            return;
        }

        const reactContainer = this.#createWidgetReactContainer(tabId, initialState);
        let widget: SelfOwnedWidget<unknown>;
        try {
            widget = widgetClass.fromJSON(reactContainer);
        } catch (error) {
            // Callers decide whether the failure aborts their flow; the error
            // stand-in is what widget hosts render either way, and it keeps
            // the persisted state intact for serialization.
            this.#setEntry(tabId, {
                type,
                widget: new ErrorWidgetModel({
                    container: this,
                    tabId,
                    widgetType: type,
                    error: asError(error),
                    persistedState: initialState,
                    logger: this.logger,
                }),
                reactContainer,
            });
            throw error;
        }

        this.#setEntry(tabId, {
            type,
            widget,
            reactContainer,
        });
    }

    #createWidgetReactContainer(tabId: WidgetId, initialState: unknown): ReactWidgetContainer {
        return this.reactWidgetContainerFactory({
            widgetId: tabId,
            widgetContainer: this,
            getInitialData: () => initialState,
        });
    }

    #setEntry(tabId: WidgetId, entry: SelfOwnedWidgetEntry): void {
        const previous = this.#selfOwnedWidgetEntries.get(tabId);
        this.#selfOwnedWidgetEntries.set(tabId, entry);
        if (previous) {
            const widget = previous.widget as SelfOwnedWidget<unknown> & Partial<Disposable>;
            widget[Symbol.dispose]?.();
        }
        this.#publishWidgets();
    }

    /**
     * Swap instances after an entitlement change: placeholders (and errored
     * stand-ins) of newly allowed types are replaced by fresh real models
     * built from their held state; real models of no-longer-allowed types are
     * serialized back into a placeholder so no state is lost.
     */
    #reconcileEntitlements(allowed: ReadonlySet<WidgetType>): void {
        for (const [tabId, entry] of [...this.#selfOwnedWidgetEntries]) {
            const isStandIn =
                entry.widget instanceof PlaceholderWidgetModel || entry.widget instanceof ErrorWidgetModel;
            const isAllowed = allowed.has(entry.type);
            if (isStandIn === !isAllowed) continue;

            const widgetClass = this.selfOwnedWidgetRegistry.get(entry.type);
            if (!widgetClass) continue;

            // Serialize-back: a real model's current state seeds the
            // placeholder; a stand-in's held state seeds the real model.
            const persistedState = entry.widget.toJSON();
            try {
                this.#registerSelfOwnedWidget(entry.type, widgetClass, tabId, persistedState);
            } catch (error) {
                // The error stand-in is already registered; keep reconciling
                // the remaining tabs.
                this.logger.error('Self-owned widget failed to instantiate after entitlement change', {
                    error: asError(error),
                    tabId,
                    widgetType: entry.type,
                });
            }
        }
    }

    #disposeWidget(tabId: WidgetId, entry: SelfOwnedWidgetEntry): void {
        this.#selfOwnedWidgetEntries.delete(tabId);
        const widget = entry.widget as SelfOwnedWidget<unknown> & Partial<Disposable>;
        widget[Symbol.dispose]?.();
        this.#publishWidgets();
    }

    #disposeAllWidgets(): void {
        for (const [tabId, entry] of this.#selfOwnedWidgetEntries) {
            this.#disposeWidget(tabId, entry);
        }
    }

    #publishWidgets(): void {
        const next = new Map<WidgetId, SelfOwnedWidget<unknown>>();
        for (const [id, entry] of this.#selfOwnedWidgetEntries) {
            next.set(id, entry.widget);
        }
        batch(() => {
            this.#widgets.value = next;
            this.#model?.setSelfOwnedWidgets(next);
        });
    }
}
